import { useMemo } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import type { Entity } from '../state/store';
import { triangulatePolygon } from '../core/commands/geometryMath';
import type { Pt3 } from '../core/commands/geometryMath';
import { gradientContext, gradientStripCanvas, gradientT, type GradientData } from './gradientFill';

/**
 * Gradient hatch fill (Faz 7.6): the boundary triangulates into a flat
 * mesh whose UVs carry the OCS shader projection (linear axis projection /
 * radial distance), and a 256x1 canvas strip provides the profile+color
 * ramp. The texture rebuilds whenever the gradient data signature changes
 * (the phase risk note: edits must regenerate the strip — the memo key IS
 * the data hash).
 */
export function GradientHatchFill({ entity, color }: { entity: Entity; color: string }) {
    const boundary = (entity.data.boundary as number[][]) ?? [];
    const gradient = entity.data.gradient as GradientData | undefined;

    const mesh = useMemo(() => {
        if (!gradient || boundary.length < 3) return null;
        const ring = boundary.map((p) => [p[0], p[1], p[2] ?? 0] as Pt3);
        const indices = triangulatePolygon(ring);
        if (indices.length === 0) return null;
        const ctx = gradientContext(gradient, ring);
        // UV.x carries the projected gradient coordinate per vertex.
        const uvs: number[] = [];
        ring.forEach((p) => {
            uvs.push(gradientT(gradient, p[0], p[1], ctx), 0.5);
        });
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(
            ring.flatMap((p) => [p[0], p[1], p[2]]), 3
        ));
        geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
        geometry.setIndex(indices.flat());
        return { geometry, signature: JSON.stringify(gradient) };
    }, [boundary, gradient]);

    const texture = useMemo(() => {
        if (!mesh || !gradient) return null;
        const canvas = gradientStripCanvas(gradient);
        const tex = new THREE.CanvasTexture(canvas);
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.wrapS = THREE.ClampToEdgeWrapping;
        tex.needsUpdate = true;
        return tex;
    }, [mesh, gradient, mesh?.signature]);

    const ring = useMemo(() => boundary.map((p) => new THREE.Vector3(p[0], p[1], p[2] ?? 0)), [boundary]);
    if (!mesh || !texture || ring.length < 3) return null;

    return (
        <group>
            <mesh geometry={mesh.geometry}>
                <meshBasicMaterial map={texture} side={THREE.DoubleSide} toneMapped={false} />
            </mesh>
            <Line points={ring} color={color} lineWidth={1.5} />
        </group>
    );
}
